import { App, Button, Empty, Form, Input, Space, Tag, Typography, theme as antdTheme } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { setupApi, type SystemSetting } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const QK = ['setup', 'system-settings'] as const;

function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\bpct\b/gi, '%')
    .replace(/(^| )([a-z])/g, (_, sp, ch) => sp + ch.toUpperCase());
}

export function SystemSettingsPage() {
  const { message } = App.useApp();
  const { token } = antdTheme.useToken();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('SETUP:EDIT'));
  const [form] = Form.useForm<Record<string, string>>();

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: QK,
    queryFn: setupApi.listSystemSettings,
  });

  useEffect(() => {
    if (data) {
      const initial: Record<string, string> = {};
      data.forEach((s) => { initial[s.key] = s.value; });
      form.setFieldsValue(initial);
    }
  }, [data, form]);

  const save = useMutation({
    mutationFn: async (values: Record<string, string>) => {
      const changed: Record<string, string> = {};
      (data ?? []).forEach((s) => {
        const v = (values[s.key] ?? '').toString();
        if (v !== s.value) changed[s.key] = v;
      });
      if (Object.keys(changed).length === 0) return [];
      return setupApi.updateSystemSettings(changed);
    },
    onSuccess: (updated) => {
      if (updated.length === 0) {
        message.info('No changes to save');
        return;
      }
      message.success(`${updated.length} setting${updated.length === 1 ? '' : 's'} updated`);
      qc.invalidateQueries({ queryKey: QK });
    },
    onError: (e) => message.error(extractError(e, 'Failed to save settings')),
  });

  const settings = useMemo<SystemSetting[]>(() => data ?? [], [data]);

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Setup / Administration"
        title="System settings"
        description="Global configuration values used across the application. Edit with care — most are referenced by automated processes."
        count={{ value: settings.length, label: 'settings' }}
        actions={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isFetching && !isLoading}
            >
              Reload
            </Button>
            {canEdit && (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => form.submit()}
                loading={save.isPending}
              >
                Save changes
              </Button>
            )}
          </Space>
        }
      />

      {!isLoading && settings.length === 0 ? (
        <Empty description="No system settings configured" />
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
          disabled={!canEdit}
        >
          <div
            style={{
              display: 'grid',
              gap: 0,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              overflow: 'hidden',
              background: token.colorBgContainer,
            }}
          >
            {settings.map((s, idx) => (
              <div
                key={s.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 280px',
                  gap: 20,
                  padding: '18px 20px',
                  borderTop: idx === 0 ? 'none' : `1px solid ${token.colorBorderSecondary}`,
                  alignItems: 'start',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Space size={8} wrap>
                    <Typography.Text strong style={{ fontSize: 14 }}>
                      {humanizeKey(s.key)}
                    </Typography.Text>
                    <Tag style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11 }}>
                      {s.key}
                    </Tag>
                  </Space>
                  {s.description && (
                    <Typography.Paragraph
                      type="secondary"
                      style={{ marginTop: 4, marginBottom: 0, fontSize: 13 }}
                    >
                      {s.description}
                    </Typography.Paragraph>
                  )}
                  {s.updatedAt && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Last updated {dayjs(s.updatedAt).format('D MMM YYYY, HH:mm')}
                      {s.updatedBy ? ` · user #${s.updatedBy}` : ''}
                    </Typography.Text>
                  )}
                </div>
                <Form.Item
                  name={s.key}
                  initialValue={s.value}
                  rules={[{ required: true, message: 'Value is required' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Value" />
                </Form.Item>
              </div>
            ))}
          </div>
        </Form>
      )}
    </div>
  );
}
